import { useState } from 'react';
import { Icon } from '../utils/icons';

interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  className?: string;
}

/**
 * Generate 256 colors in a 16x16 grid
 * Colors are evenly distributed across the color spectrum
 * Uses a better algorithm to ensure good color variety
 */
function generate256Colors(): string[] {
  const colors: string[] = [];
  
  // Generate colors with good distribution across the RGB spectrum
  // Using 16 steps for each RGB component (0, 17, 34, 51, 68, 85, 102, 119, 136, 153, 170, 187, 204, 221, 238, 255)
  const steps = [0, 17, 34, 51, 68, 85, 102, 119, 136, 153, 170, 187, 204, 221, 238, 255];
  
  // Generate 256 colors (16x16 grid) with better distribution
  // Strategy: Create 16 rows, each with 16 colors that vary smoothly
  // Use different patterns to ensure good color variety across the spectrum
  for (let row = 0; row < 16; row++) {
    for (let col = 0; col < 16; col++) {
      // Create variation by using different patterns for R, G, B
      // This ensures we get a good spread of colors across the spectrum
      const red = steps[row];
      const green = steps[col];
      // Use a different pattern for blue to create more variety
      // This creates a diagonal pattern that gives better color distribution
      const blue = steps[(row * 2 + col) % 16];
      
      const hex = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;
      colors.push(hex);
    }
  }
  
  return colors;
}

export default function ColorPicker({ value, onChange, label, className = '' }: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const colors = generate256Colors();
  
  // Convert hex to RGB for better color display
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16),
        }
      : { r: 0, g: 0, b: 0 };
  };
  
  const rgb = hexToRgb(value);
  const isLight = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000 > 128;
  
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
        </label>
      )}
      
      {/* Color Preview Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full h-12 border-2 border-gray-300 rounded-sm cursor-pointer touch-manipulation active:scale-95 transition-all flex items-center justify-between px-3 min-h-[48px]"
        style={{
          backgroundColor: value,
          borderColor: value,
        }}
        aria-label="Select color"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-sm border-2 shadow-sm"
            style={{
              backgroundColor: value,
              borderColor: isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.3)',
            }}
          />
          <span className="text-sm font-mono text-gray-700" style={{ color: isLight ? '#000' : '#fff' }}>
            {value.toUpperCase()}
          </span>
        </div>
        <span className="text-gray-500 text-sm">{isOpen ? '▼' : '▶'}</span>
      </button>
      
      {/* Color Grid */}
      {isOpen && (
        <div className="mt-3 p-4 bg-white border-2 border-gray-200 rounded-sm shadow-lg">
          <div className="grid gap-1 max-h-[400px] overflow-y-auto" style={{ gridTemplateColumns: 'repeat(16, minmax(0, 1fr))' }}>
            {colors.map((color) => {
              const colorRgb = hexToRgb(color);
              const colorIsLight = (colorRgb.r * 299 + colorRgb.g * 587 + colorRgb.b * 114) / 1000 > 128;
              const isSelected = color.toLowerCase() === value.toLowerCase();
              
              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => {
                    onChange(color);
                    setIsOpen(false);
                  }}
                  className={`w-full aspect-square rounded-sm border-2 transition-all touch-manipulation active:scale-90 min-h-[32px] ${
                    isSelected
                      ? 'ring-2 ring-offset-2 ring-[var(--accent)] scale-110 z-10'
                      : 'hover:scale-105'
                  }`}
                  style={{
                    backgroundColor: color,
                    borderColor: isSelected
                      ? '#14b8a6'
                      : colorIsLight
                      ? 'rgba(0,0,0,0.1)'
                      : 'rgba(255,255,255,0.2)',
                  }}
                  aria-label={`Select color ${color}`}
                  title={color}
                >
                  {isSelected && (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="check" size="sm" className="text-white drop-shadow-lg" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Custom Color Input (fallback) */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-xs font-medium text-gray-600 mb-2">
              Custom Color (Hex)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={value}
                onChange={(e) => {
                  const hex = e.target.value;
                  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
                    onChange(hex);
                  }
                }}
                placeholder="#000000"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent text-sm font-mono min-h-[44px]"
                maxLength={7}
              />
              <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-16 h-11 border border-gray-300 rounded-sm cursor-pointer touch-manipulation min-h-[44px]"
                aria-label="Custom color picker"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
